import * as XLSX from 'xlsx'
import type { ThemeAIRunResultsResponse, ThemeAITheme } from '../types/gotypes'
import { buildThemeDistribution } from './themeAIDistribution'
import { stampLine, type OutputStamp } from './outputStamp'

/**
 * The run's results as an Excel workbook: one sheet per breakdown (themes,
 * and sentiment and emotion when coded), each a table of count and % of
 * responses. Matt, 21 Sep: "downloading the Excel data table, because that
 * might be all I need to do." Themes are multi-response, so % can sum past 100.
 */
export function buildThemeAIResultsWorkbook(
  results: ThemeAIRunResultsResponse,
  frameThemes: ThemeAITheme[] | undefined,
  stamp?: OutputStamp,
): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new()
  // The stamp goes two rows under each table (#1650), never above it, so a
  // copied range does not shift.
  const line = stamp ? stampLine(stamp) : ''
  if (line) workbook.Props = { Author: stamp?.who || undefined, Comments: line }
  const sheet = (rows: { name: string; count: number; pct: number }[], label: string) => {
    const data = [
      [label, 'Responses', '% of responses'],
      ...rows.map((row) => [row.name, row.count, row.pct / 100]),
      [],
      ['Base: all coded responses', results.total_records, null],
      ...(line ? [[], [line]] : []),
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    for (let r = 1; r <= rows.length; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: 2 })]
      if (cell) cell.z = '0.0%'
    }
    ws['!cols'] = [{ wch: 40 }, { wch: 12 }, { wch: 16 }]
    return ws
  }
  XLSX.utils.book_append_sheet(workbook, sheet(buildThemeDistribution(results.themes, frameThemes), 'Theme'), 'Themes')
  if (results.sentiment?.length) {
    XLSX.utils.book_append_sheet(workbook, sheet(buildThemeDistribution(results.sentiment, undefined), 'Sentiment'), 'Sentiment')
  }
  if (results.emotion?.length) {
    XLSX.utils.book_append_sheet(workbook, sheet(buildThemeDistribution(results.emotion, undefined), 'Emotion'), 'Emotion')
  }
  return workbook
}

export function downloadThemeAIResultsExcel(
  results: ThemeAIRunResultsResponse,
  frameThemes: ThemeAITheme[] | undefined,
  fileName: string,
  stamp?: OutputStamp,
): void {
  XLSX.writeFile(buildThemeAIResultsWorkbook(results, frameThemes, stamp), fileName)
}
